// Single public case study.
import { createFileRoute } from "@tanstack/react-router";
import { CaseStudyDetailView } from "@/components/archive/CaseStudyDetailView";
import { getCaseStudyMetaFn, getCaseStudyFn } from "@/lib/case-study.functions";
import { buildCaseStudyEditorial, SITE } from "@/lib/case-study-editorial";
import { caseStudyCardKey } from "@/lib/case-study-card";

const FALLBACK_TITLE = "XAUUSD Case Study Replay | XAU Pro Academy";
const FALLBACK_DESC =
  "Candle by candle replay of a closed XAUUSD decision: entry, stop, targets and the outcome in R, recorded exactly as it traded.";

export const Route = createFileRoute("/case-studies/$id")({
  // Full detail is loaded on the server too, so crawlers receive the complete
  // report in the first HTML instead of a loading placeholder (Soft 404).
  loader: async ({ params }) => {
    const [meta, detail] = await Promise.all([
      getCaseStudyMetaFn({ data: { id: params.id } }),
      getCaseStudyFn({ data: { id: params.id } }).catch(() => null),
    ]);
    return meta ? { ...meta, detail } : null;
  },
  head: ({ params, loaderData }) => {
    const url = `${SITE}/case-studies/${encodeURIComponent(params.id)}`;
    const ed = loaderData ? buildCaseStudyEditorial(loaderData.trade, loaderData.netR) : null;
    const title = ed?.title ?? FALLBACK_TITLE;
    const description = ed?.description ?? FALLBACK_DESC;
    const t = loaderData?.trade;
    const cardUrl = `${SITE}/api/public/case-study-card/${caseStudyCardKey(params.id)}.jpg`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "XAU Pro Academy" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        ...(loaderData
          ? (() => {
              const img = `${cardUrl}?v=${loaderData.cardVersion ?? "pending"}`;
              return [
                { property: "og:image", content: img },
                { property: "og:image:width", content: "1200" },
                { property: "og:image:height", content: "630" },
                { property: "og:image:alt", content: title },
                { name: "twitter:image", content: img },
              ];
            })()
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: ed && t
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@graph": [
                  {
                    "@type": "Article",
                    headline: ed.h1,
                    description,
                    datePublished: t.closedAt,
                    dateModified: t.closedAt,
                    mainEntityOfPage: url,
                    image: [cardUrl],
                    about: ["XAUUSD", "Gold", "Smart Money Concepts", "Trade case study"],
                    articleSection: ed.label,
                    author: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
                    publisher: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
                  },
                  {
                    "@type": "FAQPage",
                    mainEntity: ed.facts.map((f) => ({
                      "@type": "Question",
                      name: f.q,
                      acceptedAnswer: { "@type": "Answer", text: f.a },
                    })),
                  },
                  {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                      { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                      { "@type": "ListItem", position: 2, name: "Case Studies", item: `${SITE}/case-studies` },
                      { "@type": "ListItem", position: 3, name: ed.h1, item: url },
                    ],
                  },
                ],
              }),
            },
          ]
        : [],
    };
  },
  component: CaseStudyDetailPage,
});

function CaseStudyDetailPage() {
  const { id } = Route.useParams();
  const loaderData = Route.useLoaderData();
  return <CaseStudyDetailView id={id} initialData={loaderData?.detail ?? undefined} />;
}
